import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Mail, Send, CheckCircle2, XCircle, AlertTriangle, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Handful of one-click SMTP presets so admins don't have to remember host/port.
const PRESETS = {
    gmail: {
        label: "Gmail / Google Workspace",
        host: "smtp.gmail.com", port: 587, use_tls: true, use_ssl: false,
        note: "Duhet Gmail App Password (jo passwordi i llogarisë). Aktivizoni 2FA në llogari, pastaj gjeneroni App Password.",
        help_url: "https://myaccount.google.com/apppasswords",
    },
    outlook: {
        label: "Outlook / Microsoft 365",
        host: "smtp.office365.com", port: 587, use_tls: true, use_ssl: false,
        note: "Për Microsoft 365, App Password mund të kërkohet nëse 2FA është aktivizuar.",
        help_url: "https://support.microsoft.com/account-billing/manage-app-passwords-for-two-step-verification-d6dc8c6d-4bf7-4851-ad95-6d07799387e9",
    },
    zoho: {
        label: "Zoho Mail",
        host: "smtp.zoho.com", port: 587, use_tls: true, use_ssl: false,
        note: "Përdor Zoho App Password në vend të fjalëkalimit të llogarisë.",
        help_url: "https://www.zoho.com/mail/help/adminconsole/two-factor-authentication.html#alink7",
    },
    custom: {
        label: "SMTP i personalizuar",
        host: "", port: 587, use_tls: true, use_ssl: false,
        note: "Vendos të dhënat e SMTP-së së domain-it tënd.",
        help_url: null,
    },
};

export default function EmailSettings({ embedded = false }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [preset, setPreset] = useState("gmail");
    const [testTo, setTestTo] = useState("");
    const [form, setForm] = useState({
        enabled: false,
        host: "smtp.gmail.com",
        port: 587,
        username: "",
        password: "",
        keep_existing_password: false,
        from_email: "",
        from_name: "",
        use_tls: true,
        use_ssl: false,
    });
    const [meta, setMeta] = useState({});

    const applyPreset = (p) => {
        setPreset(p);
        const pr = PRESETS[p];
        setForm((f) => ({ ...f, host: pr.host, port: pr.port, use_tls: pr.use_tls, use_ssl: pr.use_ssl }));
    };

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/settings/smtp");
            setForm({
                enabled: data.enabled,
                host: data.host || "smtp.gmail.com",
                port: data.port || 587,
                username: data.username || "",
                password: "",
                keep_existing_password: !!data.password_masked,
                from_email: data.from_email || "",
                from_name: data.from_name || "",
                use_tls: data.use_tls, use_ssl: data.use_ssl,
            });
            setMeta({
                password_masked: data.password_masked,
                verified_at: data.verified_at,
                last_test_at: data.last_test_at,
                last_test_ok: data.last_test_ok,
                last_test_error: data.last_test_error,
            });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ngarkim SMTP dështoi");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const save = async () => {
        if (!form.host || !form.from_email) {
            toast.error("Host dhe From Email janë të detyrueshme");
            return;
        }
        if (form.enabled && !meta.password_masked && !form.password) {
            toast.error("Për të aktivizuar, ju duhet një fjalëkalim");
            return;
        }
        setSaving(true);
        try {
            await api.put("/settings/smtp", form);
            toast.success("Konfigurimi u ruajt");
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ruajtja dështoi");
        } finally { setSaving(false); }
    };

    const runTest = async () => {
        if (!testTo) { toast.error("Vendos një email për testin"); return; }
        setTesting(true);
        try {
            const payload = {
                to: testTo,
                preset: "custom",
                host: form.host, port: form.port,
                username: form.username,
                password: form.keep_existing_password ? undefined : form.password,
                from_email: form.from_email, from_name: form.from_name,
                use_tls: form.use_tls, use_ssl: form.use_ssl,
            };
            // If user wants to test the already-saved password, use preset=saved
            if (form.keep_existing_password && !form.password) {
                payload.preset = "saved";
            }
            const { data } = await api.post("/settings/smtp/test", payload);
            if (data.ok) {
                toast.success(`Email u dërgua në ${data.sent_to}`);
            } else {
                toast.error(data.message || "Testi dështoi");
            }
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Testi dështoi");
        } finally { setTesting(false); }
    };

    const currentPreset = PRESETS[preset];

    return (
        <>
            {!embedded && (
                <Topbar
                    title="Cilësimet e Email"
                    subtitle="Konfiguroni SMTP-në e hotelit tuaj për email konfirmimesh"
                />
            )}
            <div className={embedded ? "mx-auto flex max-w-3xl flex-col gap-4" : "mx-auto flex max-w-3xl flex-col gap-4 p-6"}>
                {/* Info banner */}
                <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-[13px] text-blue-900">
                    <ShieldCheck size={18} className="mt-0.5 shrink-0 text-blue-600" />
                    <div>
                        <div className="font-semibold">Email nga domain-i juaj</div>
                        <div className="mt-0.5 text-blue-800/90">
                            Konfirmimet e rezervimit, kujtesat dhe faturat dërgohen nga adresa juaj (
                            <b>{form.from_email || "info@hoteli.al"}</b>), jo nga Akomoda. Kjo përmirëson
                            besueshmërinë dhe deliverability-në. Password-i ruhet i enkriptuar (Fernet).
                        </div>
                        <div className="mt-1 text-[11.5px] text-blue-700">
                            Për <b>Reset Password</b> të stafit, sistemi përdor gjithmonë SMTP-në e platformës Akomoda.
                        </div>
                    </div>
                </div>

                {/* Preset picker */}
                <div className="rounded-xl border border-border bg-card p-5">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <div className="text-[15px] font-semibold text-[#1e3c72]">Ofruesi i email-it</div>
                            <div className="text-[11px] text-muted-foreground">Zgjidh një preset për të vendosur automatikisht host/port</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                        {Object.entries(PRESETS).map(([k, p]) => (
                            <button
                                key={k}
                                onClick={() => applyPreset(k)}
                                className={`rounded-lg border px-3 py-2 text-left text-[12px] font-semibold transition ${preset === k
                                    ? "border-[#ff7b02] bg-orange-50 text-[#ff7b02] shadow-sm"
                                    : "border-border bg-white text-foreground hover:border-[#ff7b02]/40"}`}
                                data-testid={`smtp-preset-${k}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {currentPreset.note && (
                        <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-[12px] text-amber-900">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                            <div className="flex-1">
                                {currentPreset.note}
                                {currentPreset.help_url && (
                                    <a href={currentPreset.help_url} target="_blank" rel="noreferrer"
                                       className="ml-1 inline-flex items-center gap-1 font-semibold text-amber-800 hover:underline">
                                        Udhëzuesi <ExternalLink size={11} />
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Form */}
                <div className="rounded-xl border border-border bg-card p-5">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <div className="text-[15px] font-semibold text-[#1e3c72]">Konfigurimi SMTP</div>
                            <div className="text-[11px] text-muted-foreground">Këto të dhëna përdoren për të dërguar email</div>
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 text-[12px] font-semibold">
                            <input
                                type="checkbox"
                                checked={form.enabled}
                                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                                className="h-4 w-4 accent-[#ff7b02]"
                                data-testid="smtp-enabled"
                            />
                            {form.enabled ? "Aktiv" : "Joaktiv"}
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Host (server SMTP)">
                            <input className="akm-input" value={form.host}
                                   onChange={(e) => setForm({ ...form, host: e.target.value })}
                                   data-testid="smtp-host" />
                        </Field>
                        <Field label="Porti">
                            <input className="akm-input" type="number"
                                   value={form.port}
                                   onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
                                   data-testid="smtp-port" />
                        </Field>
                        <Field label="Username / Email">
                            <input className="akm-input" value={form.username} placeholder="info@hoteli.al"
                                   onChange={(e) => setForm({ ...form, username: e.target.value })}
                                   data-testid="smtp-username" />
                        </Field>
                        <Field label={meta.password_masked ? "Password (le bosh për të mbajtur)" : "Password / App Password"}>
                            <input className="akm-input" type="password"
                                   placeholder={meta.password_masked || "••••••••••••"}
                                   value={form.password}
                                   onChange={(e) => setForm({
                                       ...form, password: e.target.value,
                                       keep_existing_password: !e.target.value,
                                   })}
                                   data-testid="smtp-password" />
                        </Field>
                        <Field label="From Email (dërguesi)">
                            <input className="akm-input" value={form.from_email} placeholder="info@hoteli.al"
                                   onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                                   data-testid="smtp-from-email" />
                        </Field>
                        <Field label="From Name (emri i dërguesit)">
                            <input className="akm-input" value={form.from_name} placeholder="Hotel Panorama"
                                   onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                                   data-testid="smtp-from-name" />
                        </Field>
                    </div>

                    <div className="mt-3 flex items-center gap-4 text-[12px]">
                        <label className="flex cursor-pointer items-center gap-1.5">
                            <input type="checkbox" checked={form.use_tls}
                                   onChange={(e) => setForm({ ...form, use_tls: e.target.checked, use_ssl: e.target.checked ? false : form.use_ssl })}
                                   className="h-4 w-4 accent-[#ff7b02]" />
                            STARTTLS (port 587)
                        </label>
                        <label className="flex cursor-pointer items-center gap-1.5">
                            <input type="checkbox" checked={form.use_ssl}
                                   onChange={(e) => setForm({ ...form, use_ssl: e.target.checked, use_tls: e.target.checked ? false : form.use_tls })}
                                   className="h-4 w-4 accent-[#ff7b02]" />
                            SSL i drejtpërdrejtë (port 465)
                        </label>
                    </div>

                    {/* Status pill */}
                    {(meta.verified_at || meta.last_test_at) && (
                        <div className="mt-4 flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-[11.5px]" data-testid="smtp-status">
                            {meta.last_test_ok ? (
                                <><CheckCircle2 size={14} className="text-emerald-600" />
                                    <span className="font-semibold text-emerald-800">Verifikuar</span></>
                            ) : meta.last_test_ok === false ? (
                                <><XCircle size={14} className="text-red-600" />
                                    <span className="font-semibold text-red-800">Testi i fundit dështoi</span></>
                            ) : (
                                <><AlertTriangle size={14} className="text-amber-600" />
                                    <span className="font-semibold text-amber-800">Pa testuar</span></>
                            )}
                            <span className="text-muted-foreground">
                                {meta.last_test_at && `· ${new Date(meta.last_test_at).toLocaleString("sq-AL")}`}
                            </span>
                            {meta.last_test_error && (
                                <span className="ml-2 truncate text-red-700 italic" title={meta.last_test_error}>{meta.last_test_error}</span>
                            )}
                        </div>
                    )}

                    <div className="mt-5 flex flex-wrap items-center gap-2">
                        <button onClick={save} disabled={saving} className="akm-btn-primary" data-testid="smtp-save">
                            {saving ? "Duke ruajtur…" : "Ruaj"}
                        </button>
                        <div className="flex flex-1 items-center justify-end gap-2">
                            <input
                                className="akm-input h-9 w-52 text-[12px]"
                                placeholder="test@shembull.al"
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                data-testid="smtp-test-to"
                            />
                            <button onClick={runTest} disabled={testing || !testTo} className="akm-btn-ghost gap-1 border border-border" data-testid="smtp-test-send">
                                <Send size={13} /> {testing ? "Duke dërguar…" : "Dërgo test"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Which emails use this SMTP */}
                <div className="rounded-xl border border-border bg-card p-5">
                    <div className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-[#1e3c72]">
                        <Mail size={14} /> Çfarë dërgohet nga SMTP-ja juaj
                    </div>
                    <ul className="space-y-1.5 text-[12px] text-foreground">
                        <li>• Konfirmimet e rezervimit nga Booking Engine</li>
                        <li>• Konfirmimet e rezervimit grup (multi-room)</li>
                        <li>• Kujtesa check-in / check-out</li>
                        <li>• Fatura PDF (folio + group folio)</li>
                        <li>• Kërkesa mbledhëse (survey) pas qëndrimit</li>
                    </ul>
                    <div className="mt-3 border-t border-border pt-3 text-[11px] text-muted-foreground">
                        <b>Nuk përfshihet</b>: Reset password për stafin dhe email për account të ri —
                        këto dërgohen nga SMTP-ja qendrore e Akomodës për të garantuar aksesin edhe kur
                        SMTP-ja juaj është e paaktivizuar ose me probleme.
                    </div>
                </div>
            </div>
        </>
    );
}


function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}
